import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUsers, 
  FaChartLine, 
  FaTasks, 
  FaChartBar, 
  FaBell, 
  FaUserShield,
  FaMoneyBillWave,
  FaCog,
  FaClipboardList
} from 'react-icons/fa';
import './Features.css';

import crm1 from '../../assets/crm/crm1.png';
import raporlarVideo from '../../assets/crm/raporlar.mp4';
import iisPlani from '../../assets/modüller/iisplani.png';
import gorevAtamasi from '../../assets/modüller/gorevatamasi.png';
import musteri from '../../assets/modüller/musteri.png';
import satis from '../../assets/modüller/satis.png';
import raporlar from '../../assets/modüller/rapor.png';
import bildirimler from '../../assets/modüller/bildiirm.png';
import izinler from '../../assets/modüller/izinler.png';
import ozellestirilebilirlik from '../../assets/modüller/ozellestirilebilirlik.png';

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(null);
  const videoRef = useRef(null);

  const mediaByTitle = useMemo(
    () => ({
      'Müşteri Yönetimi': { kind: 'image', src: musteri },
      'Satış Yönetimi': { kind: 'image', src: satis },
      'Görev Takibi': { kind: 'image', src: gorevAtamasi },
      'İş Planı Takibi': { kind: 'image', src: iisPlani },
      // Modalda raporlama videosu göster
      'Raporlama ve Analiz': { kind: 'video', src: raporlarVideo },
      'Akıllı Bildirimler': { kind: 'image', src: bildirimler },
      'Ekip Yönetimi': { kind: 'image', src: izinler },
      'Finansal Takip': { kind: 'image', src: raporlar },
      'Özelleştirilebilirlik': { kind: 'image', src: ozellestirilebilirlik }
    }),
    []
  );

  const features = useMemo(
    () => [
      {
        icon: <FaUsers />,
        title: 'Müşteri Yönetimi',
        description:
          'Tüm müşteri bilgilerinizi tek yerde toplayın. Müşteri geçmişi, etkileşim takibi ve otomatik hatırlatmalar ile müşteri ilişkilerinizi güçlendirin.',
        color: '#52a6c2'
      },
      {
        icon: <FaChartLine />,
        title: 'Satış Yönetimi',
        description:
          'Satış süreçlerinizi takip edin, satış görüşmelerini kaydedin ve detaylı satış raporları ile performansınızı analiz edin.',
        color: '#ff751f'
      },
      {
        icon: <FaTasks />,
        title: 'Görev Takibi',
        description:
          'Görev atama ve takip sistemi ile görevlerinizi yönetin. Önceliklendirme ve durum yönetimi ile verimliliğinizi artırın.',
        color: '#161b2d'
      },
      {
        icon: <FaClipboardList />,
        title: 'İş Planı Takibi',
        description:
          'İş planlarınızı oluşturun ve takip edin. Detaylı planlama ve takip özellikleri ile iş süreçlerinizi optimize edin.',
        color: '#161b2d'
      },
      {
        icon: <FaChartBar />,
        title: 'Raporlama ve Analiz',
        description:
          'Gerçek zamanlı dashboard, detaylı raporlar ve Excel/PDF export özellikleri ile işinizi veri odaklı yönetin.',
        color: '#52a6c2'
      },
      {
        icon: <FaBell />,
        title: 'Akıllı Bildirimler',
        description:
          'Otomatik bildirimler, görev hatırlatmaları ve önemli güncellemeler ile hiçbir şeyi kaçırmayın.',
        color: '#ff751f'
      },
      {
        icon: <FaUserShield />,
        title: 'Ekip Yönetimi',
        description:
          'Personel yönetimi, izin ve avans takibi, performans analizi ile ekibinizi etkili bir şekilde yönetin.',
        color: '#161b2d'
      },
      {
        icon: <FaMoneyBillWave />,
        title: 'Finansal Takip',
        description:
          'Bütçe takibi, tahsilat detayları ve finansal raporlar ile mali durumunuzu her zaman kontrol altında tutun.',
        color: '#52a6c2'
      },
      {
        icon: <FaCog />,
        title: 'Özelleştirilebilirlik',
        description:
          'İşletmenizin ihtiyaçlarına göre özelleştirilebilir menü yönetimi ve yetkilendirme sistemi ile esnek bir yapı.',
        color: '#ff751f'
      }
    ],
    []
  );

  useEffect(() => {
    if (!activeFeature) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveFeature(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeFeature]);

  const enforceVideoMuted = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.volume = 0;
  };

  useEffect(() => {
    // Modal açılırken (ve render sonrası) video varsa sesi kilitle
    enforceVideoMuted();
  }, [activeFeature]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="features section" id="guclu-moduller">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Güçlü Modüller</h2>
          <p className="section-subtitle">
            İşletmenizin ihtiyaç duyduğu tüm CRM modülleri tek bir platformda
          </p>
        </motion.div>

        <motion.div
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              variants={itemVariants}
              whileHover={{ y: -5 }}
              role="button"
              tabIndex={0}
              onClick={() => setActiveFeature(feature)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setActiveFeature(feature);
              }}
            >
              <div 
                className="feature-icon"
                style={{ color: feature.color }}
              >
                {feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {activeFeature && (
          <div
            className="feature-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={`${activeFeature.title} görseli`}
            onMouseDown={() => setActiveFeature(null)}
          >
            <div
              className="feature-modal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="feature-modal-close"
                aria-label="Kapat"
                onClick={() => setActiveFeature(null)}
              >
                ×
              </button>
              {mediaByTitle[activeFeature.title]?.kind === 'video' ? (
                <video
                  ref={videoRef}
                  className="feature-modal-media"
                  src={mediaByTitle[activeFeature.title].src}
                  autoPlay
                  loop
                  muted
                  defaultMuted
                  playsInline
                  controls
                  controlsList="nodownload noplaybackrate"
                  onPlay={enforceVideoMuted}
                  onPlaying={enforceVideoMuted}
                  onVolumeChange={enforceVideoMuted}
                />
              ) : (
                <img
                  className="feature-modal-media"
                  src={mediaByTitle[activeFeature.title]?.src || crm1}
                  alt={activeFeature.title}
                  loading="eager"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Features;

